import React from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { getControlTokens } from './controlTokens';

function ControlToggleGroup({
  context = 'filterBar',
  value,
  onChange,
  options = [],
  label,
  labelWidth = 0,
  buttonWidth,
  buttonHeight,
  mode = 'icon',
  ariaLabel,
  exclusive = true,
  lockedOptions,
  hasLockedAccess = true,
  onLockedOptionSelect,
  lockedTooltip = 'Additional access required',
  sx,
}) {
  const theme = useTheme();
  const tokens = getControlTokens(theme, context);
  const height = buttonHeight || tokens.height;
  const width = buttonWidth || tokens.buttonMinWidth;
  const resolvedAriaLabel = ariaLabel || label || 'Toggle selection';
  const handleChange = (event, nextValue) => {
    if (!hasLockedAccess && lockedOptions?.has(nextValue)) {
      onLockedOptionSelect?.(nextValue);
      return;
    }
    onChange?.(event, nextValue);
  };

  return (
    <Box
      data-control-context={context}
      sx={{ height, display: 'flex', flexDirection: 'row', ...sx }}
    >
      <Box
        sx={{
          height,
          backgroundColor: tokens.surface,
          display: 'flex',
          alignItems: 'center',
          border: `1px solid ${tokens.borderColor}`,
          borderRadius: `${tokens.radius}px`,
          overflow: 'hidden',
        }}
      >
        {labelWidth > 0 && label && (
          <Box
            data-control-toggle-label="true"
            sx={{
              width: labelWidth,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              borderRight: `1px solid ${tokens.dividerColor}`,
            }}
          >
            <Typography
              sx={{
                color: tokens.textColor,
                fontSize: tokens.labelFontSize,
                lineHeight: 1.1,
                textAlign: 'center',
              }}
            >
              {label}
            </Typography>
          </Box>
        )}
        <ToggleButtonGroup
          value={value}
          exclusive={exclusive}
          onChange={handleChange}
          size="small"
          aria-label={resolvedAriaLabel}
          sx={{
            height,
            backgroundColor: tokens.surface,
            '& .MuiToggleButtonGroup-grouped': {
              border: 'none',
              borderRadius: '0 !important',
              '&:not(:first-of-type)': {
                borderLeft: `1px solid ${tokens.dividerColor}`,
              },
            },
            '& .MuiToggleButton-root': {
              height,
              minHeight: height,
              minWidth: width,
              maxWidth: width,
              p: 0,
              textTransform: 'none',
              color: tokens.textColor,
              transition: 'background-color 0.15s ease, color 0.15s ease',
              '&:hover': {
                backgroundColor: tokens.hover,
              },
              '&.Mui-selected': {
                backgroundColor: tokens.selected,
                borderLeft: `2px solid ${tokens.selectedColor}`,
                color: tokens.selectedColor,
                '&:hover': {
                  backgroundColor: tokens.selectedHover,
                },
              },
              '&:first-of-type.Mui-selected': {
                borderLeft: labelWidth > 0 ? `2px solid ${tokens.selectedColor}` : 'none',
                position: 'relative',
                '&::before': labelWidth > 0 ? undefined : {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 2,
                  backgroundColor: tokens.selectedColor,
                },
              },
              '& svg': {
                width: tokens.iconSize,
                height: tokens.iconSize,
                fontSize: tokens.iconSize,
                display: 'block',
                flexShrink: 0,
              },
            },
          }}
        >
          {options.map((option) => {
            const optionWidth = option.buttonWidth || width;
            const isLocked = !hasLockedAccess && lockedOptions?.has(option.value);
            const content = (
              <Box
                sx={{
                  display: 'flex',
                  position: 'relative',
                  flexDirection: mode === 'iconText' || mode === 'iconOnly' ? 'row' : 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: mode === 'text' || mode === 'iconOnly' ? 0 : tokens.iconLabelGap,
                  lineHeight: 1,
                  width: '100%',
                  height: '100%',
                  minWidth: 0,
                }}
              >
                {mode !== 'text' && (
                  <Box
                    component="span"
                    sx={{
                      height: tokens.iconSize,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 0,
                      flexShrink: 0,
                    }}
                  >
                    {option.icon}
                  </Box>
                )}
                {mode !== 'iconOnly' && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: mode === 'iconText' ? tokens.optionFontSize : tokens.optionSubLabelFontSize,
                      fontWeight: 800,
                      lineHeight: 1,
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: optionWidth - 6,
                    }}
                  >
                    {option.shortLabel || option.sublabel || option.label}
                  </Typography>
                )}
                {isLocked && (
                  <LockOutlinedIcon
                    data-control-locked-icon="true"
                    sx={{
                      position: 'absolute',
                      top: 1,
                      right: 2,
                      width: 10,
                      height: 10,
                      fontSize: 10,
                    }}
                  />
                )}
              </Box>
            );

            return (
              <ToggleButton
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                aria-label={isLocked
                  ? `${option.ariaLabel || option.label || option.sublabel} — ${lockedTooltip}`
                  : option.ariaLabel || option.tooltip || option.label || option.sublabel}
                data-locked-option={isLocked ? 'true' : undefined}
                sx={{
                  minWidth: optionWidth,
                  maxWidth: optionWidth,
                  opacity: isLocked ? 0.72 : 1,
                  ...option.sx,
                }}
              >
                <Tooltip title={isLocked ? lockedTooltip : option.tooltip || option.label || option.sublabel || ''}>
                  {content}
                </Tooltip>
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
}

export default ControlToggleGroup;
